import { ApolloError } from '@apollo/client';
import {
  type GridApiPremium,
  type GridCsvExportOptions,
  type GridExcelExportOptions,
  type GridRowId,
  type GridRowModel,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect } from 'react';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { notify } from 'src/lib/notify';
import { wait } from 'src/utils/wait';
import { create } from 'zustand';
import { GRID_PRINT_PAGE_STYLE } from './gridPrintPageStyle';

export type GridExportOptions = 'csv' | 'excel' | 'print';

type GridExportState = {
  isExporting: boolean;
  exportFormat: GridExportOptions | null;
  allRowsLoaded: boolean;
  setAllRowsLoaded: (value: boolean) => void;
  setExporting: (value: boolean, format?: GridExportOptions | null) => void;
};

export const useGridExportState = create<GridExportState>((set) => ({
  isExporting: false,
  exportFormat: null,
  allRowsLoaded: false,
  setAllRowsLoaded: (value) => set({ allRowsLoaded: value }),
  setExporting: (value, format = null) =>
    set({ allRowsLoaded: false, isExporting: value, exportFormat: value ? format : null }),
}));

/** `print` must NOT get export presentation mode: MUI clones the live grid DOM for the printout and dereferences `.MuiDataGrid-contentFiller` unguarded, which the skeleton overlay unmounts. */
export const isRowSerializingExport = (format: GridExportOptions | null) => format === 'csv' || format === 'excel';

export type GridExportApi = Pick<
  GridApiPremium,
  'exportDataAsCsv' | 'exportDataAsExcel' | 'exportDataAsPrint' | 'getRowModels' | 'setRows'
> &
  Partial<Pick<GridApiPremium, 'getRowNode'>>;
export type GridExportApiRef = { current: GridExportApi | null };
/** All the complete-export check reads. Any grid flavour's API satisfies it. */
export type GridExportRowReader = Pick<GridExportApi, 'getRowModels'>;

export const EXPORT_GRID_EVENT = 'exportGrid';

export const EXPORT_SCOPE_CHANGED_MESSAGE =
  'The grid changed while the export was being prepared. Try exporting again.';
let completeExportRows: { api: GridExportApi; rows: ReadonlyMap<GridRowId, GridRowModel> } | undefined;
let installingCompleteExportRows = false;

export const exportGrid = (format: GridExportOptions) => {
  window.focus();
  const event = new CustomEvent(EXPORT_GRID_EVENT, { detail: format });
  document.dispatchEvent(event);
};

function rowSnapshotsMatch(
  currentRows: ReadonlyMap<GridRowId, GridRowModel>,
  expectedRows: ReadonlyMap<GridRowId, GridRowModel>
): boolean {
  if (currentRows.size !== expectedRows.size) return false;

  for (const [id, row] of expectedRows) {
    if (currentRows.get(id) !== row) return false;
  }

  return true;
}

export function gridHasCompleteExportRows(api: GridExportRowReader | null): boolean {
  if (api === null) return false;
  if (installingCompleteExportRows) return true;

  return completeExportRows !== undefined && rowSnapshotsMatch(api.getRowModels(), completeExportRows.rows);
}

function writeGridExport(
  api: GridExportApi,
  format: GridExportOptions,
  datedFileName: string,
  exportExcelOptions: GridExcelExportOptions,
  exportCsvOptions: GridCsvExportOptions
): void | Promise<void> {
  switch (format) {
    case 'excel':
      return api.exportDataAsExcel({ fileName: datedFileName, escapeFormulas: false, ...exportExcelOptions });
    case 'csv':
      return api.exportDataAsCsv({ fileName: datedFileName, escapeFormulas: false, ...exportCsvOptions });
    case 'print':
      return api.exportDataAsPrint({ fileName: datedFileName, pageStyle: GRID_PRINT_PAGE_STYLE });
  }
}

export default function useGridExport(
  apiRef: GridExportApiRef,
  fileName: string,
  exportExcelOptions: GridExcelExportOptions = {},
  exportCsvOptions: GridCsvExportOptions = {},
  fetchAllRows?: () => Promise<GridRowModel[]>
) {
  const gridExportState = useGridExportState();

  const exportGrid = useCallback(
    async ({ detail: format }: CustomEvent<GridExportOptions>) => {
      const api = apiRef.current;
      if (!api || useGridExportState.getState().isExporting) return;

      gridExportState.setExporting(true, format);

      const formattedDate = new Date().toISOString().replace(/:/g, '-');
      const datedFileName = `${fileName}-${formattedDate}`;
      const currentPage = fetchAllRows ? api.getRowModels() : undefined;
      let exportRows: ReadonlyMap<GridRowId, GridRowModel> | undefined;
      let operationFailed = false;

      try {
        if (fetchAllRows) {
          notify.info('Collecting all data for export...');
          const allRows = await fetchAllRows();

          if (currentPage === undefined || !rowSnapshotsMatch(api.getRowModels(), currentPage)) {
            throw new Error(EXPORT_SCOPE_CHANGED_MESSAGE);
          }

          installingCompleteExportRows = true;
          try {
            // A server-side tree re-parents a re-set row under its previous parent, so an open group's
            // rows would come back nested under a collapsed placeholder and miss the print clone.
            // Emptying such a tree first roots every export row flat and visible.
            if ([...currentPage.keys()].some((id) => (api.getRowNode?.(id)?.depth ?? 0) > 0)) api.setRows([]);
            api.setRows(allRows);
            exportRows = api.getRowModels();
            completeExportRows = { api, rows: exportRows };
          } finally {
            installingCompleteExportRows = false;
          }
          gridExportState.setAllRowsLoaded(true);

          await wait(100);

          if (!rowSnapshotsMatch(api.getRowModels(), exportRows)) {
            throw new Error(EXPORT_SCOPE_CHANGED_MESSAGE);
          }
        }

        notify.info('Data fetched. Generating file for download...');

        const write = writeGridExport(api, format, datedFileName, exportExcelOptions, exportCsvOptions);
        // Keep the legacy one-second success window, while also observing the asynchronous Excel writer.
        await Promise.all([Promise.resolve(write), wait(1_000)]);
      } catch (error) {
        operationFailed = true;
        // A failed row fetch is an Apollo error the shared error link may already have shown; the rest are ours.
        if (error instanceof ApolloError) notifyUnlessSurfaced(error, 'Could not export the data.');
        else notify.error(error);
      } finally {
        try {
          if (
            currentPage !== undefined &&
            exportRows !== undefined &&
            rowSnapshotsMatch(api.getRowModels(), exportRows)
          ) {
            api.setRows(Array.from(currentPage.values()));
          }
        } catch (restorationError) {
          if (!operationFailed) notify.error(restorationError);
        } finally {
          installingCompleteExportRows = false;
          if (completeExportRows?.api === api) completeExportRows = undefined;
          gridExportState.setExporting(false);
        }
      }
    },
    [gridExportState, fileName, fetchAllRows, apiRef, exportExcelOptions, exportCsvOptions]
  );

  const handleExportEvent = useCallback(
    (event: Event) => {
      if (event instanceof CustomEvent) void exportGrid(event);
    },
    [exportGrid]
  );

  useEffect(() => {
    document.addEventListener(EXPORT_GRID_EVENT, handleExportEvent);
    return () => document.removeEventListener(EXPORT_GRID_EVENT, handleExportEvent);
  }, [handleExportEvent]);
}
